<script lang="ts">
	// Design system components
	import {
		CardGrid,
		Container,
		ReportParameter,
		Text,
		SystemStyles,
	} from '@Shelly/shelly-design-system';
	//Helpers
	import { convert, convertCost, sortOn } from 'src/helpers/utilities';
	import {
		getDailyRangeOptions,
		getMonthsOptions,
		getTimeFormat,
		timeFormatUS,
	} from 'src/helpers/date_helper';
	import { get } from 'svelte/store';
	import { devices } from 'src/stores/lists/devices';
import { rooms } from 'src/stores/lists/rooms';
	import { load_image_from } from 'src/helpers/interface_helpers';
	import type { Room } from 'src/types/Room';
	import { getMonthlyReport } from '../Chart/chart_config';
	import { getMonthlyReportByDevices, getMonthlyTotal } from './consumption_helper';
	//Types
	import type {
		MonthlyReportByDevice,
		RequestMonthlyReport,
	} from 'src/types/requests/charts/get_stats_period';
	import type { Device } from 'src/types/Device';
	import type { SectionType } from '@Shelly/shelly-design-system/ReportParameter/ReportParameter.types';
	import { format } from 'svelte-i18n';

	export let result: RequestMonthlyReport, year: number, month: number, currency: string;

	let device: Device, highestConsumptionDevice: MonthlyReportByDevice;

	const unit = 'Wh',
		allDevices = get(devices),
		highestConsumptionDevices: MonthlyReportByDevice[] = sortOn(
			getMonthlyReportByDevices(result?.report?.totalByDevice, get(devices)),
			'consumption',
			true,
		);

	for (let i = 0; i < highestConsumptionDevices.length; i++) {
		if (typeof highestConsumptionDevice == 'undefined') {
			highestConsumptionDevice = highestConsumptionDevices[i];
		} else {
			highestConsumptionDevice =
				highestConsumptionDevices[i].consumption >= highestConsumptionDevice.consumption
					? highestConsumptionDevices[i]
					: highestConsumptionDevice;
		}

		device = allDevices[highestConsumptionDevice.id];
		if (device && !/grid_source|solar_source/.test(device?.relay_usage)) break;
	}

	const highestConsumptionRoom: MonthlyReportByDevice = sortOn(
			result?.report?.totalByRoom,
			'consumption',
			true,
		)?.[0],
		room: Room = get(rooms)[highestConsumptionRoom?.id],
		getLabel = (report: MonthlyReportByDevice): SectionType[] => {
			const label = [
				{
					icon: 'fas fa-bolt',
					iconColor: SystemStyles.GRADIENT_CHART_CONSUMED_ENERGY,
					title: convert(report?.consumption, unit),
				},
			];
			if (report?.cost) {
				label.push({
					icon: 'fas fa-coins',
					iconColor: SystemStyles.COLOR_CHART_MONEY_VALUE,
					title: convertCost(report.cost, currency),
				});
			}
			if (report?.reversed) {
				label.push({
					icon: 'fas fa-house-day',
					iconColor: SystemStyles.COLOR_SOLAR,
					title: convert(Math.abs(report.reversed), unit),
				});
			}
			//@ts-ignore
			return label;
		},
		labelListTotal: SectionType[] = [
			{
				icon: 'fas fa-bolt',
				iconColor: SystemStyles.GRADIENT_CHART_CONSUMED_ENERGY,
				title: convert(result?.report?.total, unit),
			},
		],
		mostConsumingHourData = getMonthlyReport(
			'hour',
			result?.report?.totalByHour,
			year,
			month,
			currency,
			//@ts-ignore
		).mostConsumingData,
		labelListHour = getLabel(mostConsumingHourData),
		labelListDay = getLabel(result?.report?.mostConsumingDay),
		mostConsumingWeekData = getMonthlyReport(
			'week',
			result?.report?.totalByWeekDay,
			year,
			month,
			currency,
			//@ts-ignore
		).mostConsumingData,
		mostConsumingWeekDay = mostConsumingWeekData.index,
		labelListWeekDay = getLabel(mostConsumingWeekData),
		totalByHours = getMonthlyTotal(result?.report?.totalByHour, currency),
		hourLabel = timeFormatUS()
			? getTimeFormat(mostConsumingHourData.index, null, false)
			: `${mostConsumingHourData.index} o’clock`;

	if (totalByHours.cost > 0) {
		labelListTotal.push({
			icon: 'fas fa-coins',
			iconColor: SystemStyles.COLOR_CHART_MONEY_VALUE,
			title: totalByHours.convertCost,
		});
	}
	if (result?.report?.totalReversed && result.report.totalReversed > 0) {
		labelListTotal.push({
			icon: 'fas fa-house-day',
			iconColor: SystemStyles.COLOR_SOLAR,
			title: convert(Math.abs(result.report.totalReversed), unit),
		});
	}
</script>

{#if result?.hasOwnProperty('report')}
	<Container class="mt-m mb-md-m mb-sm-s" style="background: var(--color-2)">
		<Text class="text-bold mb-m text-center"
			>{$format('monthly_reports_text_top_overview', {
				default: 'Overview',
			})}</Text
		>
		<CardGrid lg={380} sm={270} noPaddingBottom>
			<ReportParameter
				sections={labelListTotal}
				value={getMonthsOptions()[month].title}
				icon="fa-thin fa-light fa-bolt"
				description={$format('monthly_reports_desc_total_energy', {
					default: 'Total Energy',
				})}
			/>
			<ReportParameter
				sections={labelListHour}
				value={hourLabel}
				icon="fa-thin fa-clock"
				description={$format('monthly_reports_desc_average_hour', {
					default: 'Average Most Consuming Hour',
				})}
			/>
			<ReportParameter
				sections={labelListDay}
				value={result?.report?.mostConsumingDay?.day?.toString()}
				icon="fa-thin fa-calendar-day"
				description={$format('monthly_reports_desc_consuming_day', {
					default: 'Most Consuming Day',
				})}
			/>
			<ReportParameter
				sections={labelListWeekDay}
				value={getDailyRangeOptions().find((i) => i.value == mostConsumingWeekDay).title}
				icon="fa-thin fa-calendar"
				description={$format('monthly_reports_desc_average_week_day', {
					default: 'Average Most Consuming Day Of The Week',
				})}
			/>
			{#if device}
				<ReportParameter
					sections={getLabel(highestConsumptionDevice)}
					value={device?.name || 'Unidentified'}
					icon={device?.image || device?.backgroundColor ? null : 'fa-thin fa-microchip'}
					image={device?.image ? load_image_from(device.image, device?.server) : null}
					backgroundColor={device?.backgroundColor}
					description={$format('monthly_reports_desc_device_high_consumption', {
						default: 'Device With The Highest Consumption',
					})}
				/>
			{/if}
			{#if room}
				<ReportParameter
					sections={getLabel(highestConsumptionRoom)}
					value={room?.name || 'Unidentified'}
					icon={room?.image || room?.backgroundColor ? null : 'fa-thin fa-door-open'}
					image={room?.image ? load_image_from(room.image) : null}
					backgroundColor={room?.backgroundColor}
					description={$format('monthly_reports_desc_room_high_consumption', {
						default: 'Room With The Highest Consumption',
					})}
				/>
			{/if}
		</CardGrid>
	</Container>
{/if}
